import type { Json } from './database.types';
import {
  registrationFormFieldTypeSchema,
  registrationFormSchema,
  type RegistrationFormField,
  type RegistrationFormFieldType,
  type RegistrationFormResponses,
} from './schemas';

export const REGISTRATION_FORM_SCHEMA_VERSION = 1;

export const REGISTRATION_FORM_FIELD_TYPES = registrationFormFieldTypeSchema.options;

export type {
  RegistrationFormField,
  RegistrationFormFieldType,
  RegistrationFormResponses,
} from './schemas';

export type RegistrationFormSchema = Readonly<{
  fields: readonly RegistrationFormField[];
  version: typeof REGISTRATION_FORM_SCHEMA_VERSION;
}>;

export type RegistrationFormResponseValidationResult = Readonly<{
  errors: Record<string, string>;
  message: string | null;
  responses: RegistrationFormResponses;
  valid: boolean;
}>;

export type RegistrationFormSchemaValidationResult = Readonly<{
  error: string | null;
  valid: boolean;
}>;

export const EMPTY_REGISTRATION_FORM_SCHEMA: RegistrationFormSchema = {
  fields: [],
  version: REGISTRATION_FORM_SCHEMA_VERSION,
};

export const MAX_REGISTRATION_FORM_FIELDS = 20;
const MAX_MULTIPLE_CHOICE_OPTIONS = 12;
const SHORT_TEXT_LIMIT = 200;
const LONG_TEXT_LIMIT = 2000;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringValue(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function booleanValue(value: unknown): boolean {
  return value === true;
}

function isRegistrationFormFieldType(value: unknown): value is RegistrationFormFieldType {
  return registrationFormFieldTypeSchema.safeParse(value).success;
}

function normalizeFieldId(value: unknown, fallback: string): string {
  const raw = stringValue(value)
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '_');
  let start = 0;
  let end = raw.length;
  while (raw[start] === '_') {
    start += 1;
  }
  while (raw[end - 1] === '_') {
    end -= 1;
  }

  return raw.slice(start, end) || fallback;
}

function normalizeOptions(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const uniqueOptions: string[] = [];
  for (const option of value) {
    const normalized = stringValue(option);
    if (normalized && !uniqueOptions.includes(normalized)) {
      uniqueOptions.push(normalized);
    }
    if (uniqueOptions.length >= MAX_MULTIPLE_CHOICE_OPTIONS) {
      break;
    }
  }

  return uniqueOptions;
}

function normalizeField(rawField: unknown, index: number): RegistrationFormField | null {
  if (!isRecord(rawField) || !isRegistrationFormFieldType(rawField.type)) {
    return null;
  }

  const label = stringValue(rawField.label);
  if (!label) {
    return null;
  }

  const field: RegistrationFormField = {
    id: normalizeFieldId(rawField.id, `question_${index + 1}`),
    label,
    required: booleanValue(rawField.required),
    type: rawField.type,
  };

  if (field.type !== 'multiple_choice') {
    return field;
  }

  return {
    ...field,
    options: normalizeOptions(rawField.options),
  };
}

export function normalizeRegistrationFormSchema(value: unknown): RegistrationFormSchema {
  if (!isRecord(value)) {
    return EMPTY_REGISTRATION_FORM_SCHEMA;
  }

  const fields = Array.isArray(value.fields)
    ? value.fields
        .slice(0, MAX_REGISTRATION_FORM_FIELDS)
        .map((field, index) => normalizeField(field, index))
        .filter((field): field is RegistrationFormField => Boolean(field))
    : [];

  const seenIds = new Set<string>();
  const uniqueFields = fields.map((field, index) => {
    let id = field.id;
    if (seenIds.has(id)) {
      id = `${id}_${index + 1}`;
    }
    seenIds.add(id);
    return { ...field, id };
  });

  return {
    fields: uniqueFields,
    version: REGISTRATION_FORM_SCHEMA_VERSION,
  };
}

export function registrationFormSchemaToJson(schema: RegistrationFormSchema): Json {
  return {
    fields: schema.fields.map((field) => ({
      id: field.id,
      label: field.label.trim(),
      options: field.type === 'multiple_choice' ? [...(field.options ?? [])] : undefined,
      required: field.required,
      type: field.type,
    })),
    version: REGISTRATION_FORM_SCHEMA_VERSION,
  };
}

export function registrationFormResponsesToJson(responses: RegistrationFormResponses): Json {
  return { ...responses };
}

export function createRegistrationFormField(
  type: RegistrationFormFieldType,
  existingFields: readonly RegistrationFormField[] = []
): RegistrationFormField {
  const base = registrationFormFieldIdBase(type);
  let index = existingFields.length + 1;
  let id = `${base}_${index}`;
  const existingIds = new Set(existingFields.map((field) => field.id));
  while (existingIds.has(id)) {
    index += 1;
    id = `${base}_${index}`;
  }

  return {
    id,
    label: '',
    options: registrationFormDefaultOptions(type),
    required: false,
    type,
  };
}

function registrationFormFieldIdBase(type: RegistrationFormFieldType): string {
  switch (type) {
    case 'multiple_choice':
      return 'choice';
    case 'short_text':
      return 'short';
    case 'long_text':
      return 'long';
    default:
      return 'question';
  }
}

function registrationFormDefaultOptions(type: RegistrationFormFieldType): string[] | undefined {
  return type === 'multiple_choice' ? ['Option 1', 'Option 2'] : undefined;
}

export function validateRegistrationFormSchema(
  schema: RegistrationFormSchema
): RegistrationFormSchemaValidationResult {
  if (!registrationFormSchema.safeParse(schema).success) {
    return { error: 'Registration form schema is invalid.', valid: false };
  }
  if (schema.fields.length > MAX_REGISTRATION_FORM_FIELDS) {
    return {
      error: `Registration forms can have at most ${MAX_REGISTRATION_FORM_FIELDS} questions.`,
      valid: false,
    };
  }

  const seenIds = new Set<string>();
  for (const field of schema.fields) {
    if (!field.label.trim()) {
      return { error: 'Every registration question needs a label.', valid: false };
    }
    if (seenIds.has(field.id)) {
      return { error: 'Registration question IDs must be unique.', valid: false };
    }
    seenIds.add(field.id);
    if (field.type === 'multiple_choice') {
      const options = normalizeOptions(field.options);
      if (options.length < 2) {
        return {
          error: 'Multiple choice questions need at least two options.',
          valid: false,
        };
      }
    }
  }

  return { error: null, valid: true };
}

export function registrationFormHasQuestions(schema: RegistrationFormSchema | null): boolean {
  return Boolean(schema?.fields.length);
}

export function isRegistrationFormFieldRequired(field: RegistrationFormField): boolean {
  return field.required;
}

export function registrationFormHasRequiredFields(schema: RegistrationFormSchema): boolean {
  return schema.fields.some((field) => isRegistrationFormFieldRequired(field));
}

export function normalizeRegistrationFormResponses(value: unknown): RegistrationFormResponses {
  if (!isRecord(value)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value)
      .map(([key, answer]) => [key, stringValue(answer)] as const)
      .filter(([, answer]) => Boolean(answer))
  );
}

function registrationFormFieldResponseError(
  field: RegistrationFormField,
  value: string,
  required: boolean
): string | null {
  if (!value) {
    return required ? 'Answer this question to continue.' : null;
  }

  if (field.type === 'multiple_choice' && !normalizeOptions(field.options).includes(value)) {
    return 'Choose one of the available options.';
  }

  if (field.type === 'short_text' && value.length > SHORT_TEXT_LIMIT) {
    return `Keep this answer under ${SHORT_TEXT_LIMIT} characters.`;
  }

  if (field.type === 'long_text' && value.length > LONG_TEXT_LIMIT) {
    return `Keep this answer under ${LONG_TEXT_LIMIT} characters.`;
  }

  return null;
}

export function validateRegistrationFormResponses(
  schema: RegistrationFormSchema,
  responses: unknown
): RegistrationFormResponseValidationResult {
  const input = normalizeRegistrationFormResponses(responses);
  const nextResponses: RegistrationFormResponses = {};
  const errors: Record<string, string> = {};

  for (const field of schema.fields) {
    const value = stringValue(input[field.id]);
    const required = isRegistrationFormFieldRequired(field);
    const error = registrationFormFieldResponseError(field, value, required);

    if (error) {
      errors[field.id] = error;
      continue;
    }

    if (value) {
      nextResponses[field.id] = value;
    }
  }

  const firstError = Object.values(errors)[0] ?? null;
  return {
    errors,
    message: firstError,
    responses: nextResponses,
    valid: !firstError,
  };
}
